/**
 * Utility functions for generating clean province page URLs.
 * Handles the logic for transforming province page slugs into clean, user-friendly URLs.
 */

/**
 * Generates a clean URL for province pages based on province and page slugs.
 * 
 * @param provinceSlug - The slug of the province (e.g., "zeeland", "groningen")
 * @param pageSlug - The slug of the page (e.g., "zeeland", "zeeland-nieuws", "groningen-speerpunten")
 * @returns Clean URL for the province page
 * 
 * @example
 * generateProvincePageUrl("zeeland", "zeeland") → "/provincies/zeeland"
 * generateProvincePageUrl("zeeland", "zeeland-nieuws") → "/provincies/zeeland/nieuws"
 * generateProvincePageUrl("groningen", "groningen-speerpunten") → "/provincies/groningen/speerpunten"
 */
export function generateProvincePageUrl(provinceSlug: string, pageSlug: string): string {
  if (pageSlug === provinceSlug) {
    // Province home page: use clean URL
    return `/provincies/${provinceSlug}`;
  } else if (pageSlug.startsWith(`${provinceSlug}-`)) {
    // Province sub-page with prefix: remove province prefix for clean URL
    const pageNameWithoutPrefix = pageSlug.replace(`${provinceSlug}-`, '');
    return `/provincies/${provinceSlug}/${pageNameWithoutPrefix}`;
  } else {
    // Fallback: use full page slug (for edge cases)
    return `/provincies/${provinceSlug}/${pageSlug}`;
  }
} 